import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, CheckCircle2, Loader2, Mail, ArrowRight } from "lucide-react";
import Logo from "@/components/Logo";
import { Toaster } from "@/components/ui/sonner";
import { api } from "@/lib/api";
import { Doodle } from "@/components/marketing/Decor";

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    const v = (email || "").trim();
    if (!v || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) {
      setErr("Please enter a valid email address.");
      return;
    }
    setBusy(true);
    try {
      await api.forgotPassword(v);
      setSent(true);
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Couldn't send the reset email. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="app-light mkt-root mkt-paper-noise min-h-screen flex items-center justify-center px-4 py-10 relative overflow-hidden">
      <Toaster richColors position="top-center" />
      <Doodle.Bolt className="hidden md:block absolute right-12 top-10 h-12 w-8 text-black rotate-12" />
      <Doodle.Scribble className="hidden md:block absolute left-6 bottom-16 h-10 w-24 text-black/60 -rotate-6" />

      <div className="w-full max-w-md relative">
        <div className="flex justify-center mb-6">
          <Link to="/" aria-label="City DOOH home" className="inline-flex focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-black rounded-md"><Logo size={150} light /></Link>
        </div>
        <div className="mkt-sticker p-7 relative">
          <span className="mkt-tape absolute -top-4 left-6">Reset Password</span>
          {sent ? (
            <div className="text-center mt-3" data-testid="forgot-sent-panel">
              <div className="mx-auto h-14 w-14 rounded-full bg-[#d8f1c8] border-2 border-[#176b3a] grid place-items-center text-[#176b3a] mb-3">
                <CheckCircle2 className="h-7 w-7" />
              </div>
              <h1 className="font-display uppercase text-2xl">Check your inbox</h1>
              <p className="text-sm text-black/70 mt-2">
                If an account exists for <span className="font-bold text-black">{email}</span>, we’ve sent a reset link.
                It’s valid for <span className="font-bold text-black">1 hour</span>.
              </p>
              <p className="text-xs text-black/60 mt-3">
                Didn’t get it? Check spam, or
                <button type="button" className="ml-1 font-bold underline decoration-[var(--graffiti-yellow)] decoration-4 underline-offset-2" onClick={() => setSent(false)} data-testid="forgot-try-again">
                  try a different email
                </button>.
              </p>
              <Link to="/vendor/login" className="block mt-6" data-testid="forgot-back-to-login">
                <button className="ag-btn-ghost w-full justify-center">
                  <ArrowLeft className="h-4 w-4 mr-1" /> Back to sign in
                </button>
              </Link>
            </div>
          ) : (
            <>
              <h1 className="font-display uppercase text-3xl mt-3 leading-none">
                Forgot your <span className="mkt-highlight">password</span>?
              </h1>
              <p className="text-sm text-black/70 mt-2">
                Enter your signup email and we’ll send you a reset link.
              </p>
              <form onSubmit={submit} className="mt-6 space-y-4">
                <div className="space-y-1.5">
                  <Label className="uppercase text-[11px] font-bold tracking-wider text-black">Email</Label>
                  <div className="relative">
                    <Mail className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-black/55" />
                    <Input
                      type="email"
                      autoFocus
                      autoComplete="email"
                      className="pl-9 h-11 border-2 border-black bg-white"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@business.com"
                      data-testid="forgot-email"
                    />
                  </div>
                </div>
                {err && (
                  <p className="text-sm font-bold text-[#8b1212] bg-[#fbd5d5] border-2 border-[#8b1212] rounded-md px-3 py-2" data-testid="forgot-error">{err}</p>
                )}
                <button type="submit" className="mkt-btn w-full justify-center" disabled={busy} data-testid="forgot-submit">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : (<>Send reset link <ArrowRight className="h-4 w-4" /></>)}
                </button>
              </form>
              <div className="text-sm text-black/70 mt-5 text-center">
                Remembered it?{" "}
                <Link to="/vendor/login" className="font-bold text-black underline decoration-[var(--graffiti-yellow)] decoration-4 underline-offset-2" data-testid="forgot-signin-link">
                  Sign in
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
